import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";
import LineSignShape from "../../../../components/visuals/LineSignShape";

const TOL = 1e-7;

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}


function numberLatex(value, { mixed = false } = {}) {
  if (!Number.isFinite(value)) return String(value);
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const rounded = Math.round(abs);
  if (Math.abs(abs - rounded) < TOL) return `${sign}${rounded}`;
  for (let d = 2; d <= 24; d += 1) {
    const n = Math.round(abs * d);
    if (Math.abs(abs - n / d) < TOL) {
      const g = gcd(n, d);
      const numerator = n / g;
      const denominator = d / g;
      if (mixed && numerator > denominator) {
        const whole = Math.floor(numerator / denominator);
        const rest = numerator % denominator;
        return rest === 0
          ? `${sign}${whole}`
          : `${sign}${whole}\\dfrac{${rest}}{${denominator}}`;
      }
      return `${sign}\\dfrac{${numerator}}{${denominator}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}

function numberRaw(value) {
  if (!Number.isFinite(value)) return String(value);
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < TOL) return String(rounded);
  for (let d = 2; d <= 24; d += 1) {
    const n = Math.round(value * d);
    if (Math.abs(value - n / d) < TOL) {
      const g = gcd(n, d);
      return `${n / g}/${d / g}`;
    }
  }
  return String(Number(value.toFixed(4))).replace(".", ",");
}

function setLatex(values, options = {}) {
  const list = options.sort ? uniqueSorted(values) : values;
  return `\\{${list.map((value) => numberLatex(value, options)).join(",\\;")}\\}`;
}

function intervalLatex(expected) {
  const left = numberLatex(expected.left, { mixed: true });
  const right = numberLatex(expected.right, { mixed: true });
  return `${expected.leftClosed ? "[" : "("}${left};\\,${right}${expected.rightClosed ? "]" : ")"}`;
}

function parseFiniteSet(raw) {
  const parsed = parseIntervalInput(raw);
  if (parsed?.kind === "finite") return parsed.points;
  if (parsed?.kind === "empty") return [];
  return null;
}

function sameFiniteSet(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const A = [...a].sort((x, y) => x - y);
  const B = [...b].sort((x, y) => x - y);
  return A.every((value, index) => Math.abs(value - B[index]) < TOL);
}

function sameInterval(userParsed, expected) {
  if (!userParsed || userParsed.kind !== "intervals") return false;
  const intervals = userParsed.intervals || [];
  if (intervals.length !== 1) return false;
  const got = intervals[0];
  return (
    Math.abs(got.left - expected.left) < TOL &&
    Math.abs(got.right - expected.right) < TOL &&
    !!got.leftClosed === !!expected.leftClosed &&
    !!got.rightClosed === !!expected.rightClosed
  );
}

function uniqueSorted(values) {
  return Array.from(new Set(values.map((value) => Number(value.toFixed(10))))).sort((a, b) => a - b);
}

function makeNestedSimpleVariant() {
  const pool = [];
  for (let center = 2; center <= 14; center += 1) {
    const maxDelta = Math.min(9, center - 1);
    for (let delta = 1; delta <= maxDelta; delta += 1) {
      pool.push({ center, delta });
    }
  }
  const picked = pool[Math.floor(Math.random() * pool.length)];
  const center = picked.center;
  const delta = picked.delta;
  const high = center + delta;
  const low = center - delta;
  const solutions = uniqueSorted([-high, -low, low, high]);
  return {
    id: "nested-simple-" + center + "-" + delta,
    family: "nested",
    slot: "nested-simple",
    equation: "\\left|\\left|x\\right|-" + center + "\\right|=" + delta,
    answerKind: "finite",
    solutions,
    critical: [0, -high, -low, low, high],
    steps: [
      "\\left|x\\right|-" + center + "=" + delta + "\\quad\\text{lub}\\quad\\left|x\\right|-" + center + "=-" + delta,
      "\\left|x\\right|=" + high + "\\quad\\text{lub}\\quad\\left|x\\right|=" + low,
      "x\\in " + setLatex(solutions),
    ],
    nestedDetails: [
      {
        label: "Pierwszy przypadek",
        start: "\\left|x\\right|-" + center + "=" + delta,
        isolate: "\\left|x\\right|=" + high,
        branches: ["x=" + high, "x=-" + high],
        solutions: [high, -high],
      },
      {
        label: "Drugi przypadek",
        start: "\\left|x\\right|-" + center + "=-" + delta,
        isolate: "\\left|x\\right|=" + low,
        branches: ["x=" + low, "x=-" + low],
        solutions: [low, -low],
      },
    ],
  };
}

function linearLatex(a, b) {
  const ax = a === 1 ? "x" : a + "x";
  if (b === 0) return ax;
  return ax + (b > 0 ? "+" : "") + b;
}

function makeNestedLinearVariant() {
  const a = [1, 2, 3][Math.floor(Math.random() * 3)];
  const b = Math.floor(Math.random() * 9) - 4;
  const center = 2 + Math.floor(Math.random() * 7);
  const deltaPool = [1, 2, 3, center + 1, center + 2].filter((value, index, arr) => arr.indexOf(value) === index);
  const delta = deltaPool[Math.floor(Math.random() * deltaPool.length)];
  const high = center + delta;
  const low = center - delta;
  const linear = linearLatex(a, b);
  const solveFor = (k) => (k - b) / a;
  const highSolutions = [solveFor(high), solveFor(-high)];
  const lowSolutions = low >= 0 ? [solveFor(low), solveFor(-low)] : [];
  const solutions = uniqueSorted([...highSolutions, ...lowSolutions]);
  return {
    id: "nested-linear-" + a + "-" + b + "-" + center + "-" + delta,
    family: "nested",
    slot: "nested-linear",
    equation: "\\left|\\left|" + linear + "\\right|-" + center + "\\right|=" + delta,
    answerKind: "finite",
    solutions,
    critical: uniqueSorted([-b / a, ...solutions]),
    steps: [
      "\\left|" + linear + "\\right|-" + center + "=" + delta + "\\quad\\text{lub}\\quad\\left|" + linear + "\\right|-" + center + "=-" + delta,
      "\\left|" + linear + "\\right|=" + high + "\\quad\\text{lub}\\quad\\left|" + linear + "\\right|=" + low,
      "x\\in " + setLatex(solutions),
    ],
    nestedDetails: [
      makeLinearNestedCase("Pierwszy przypadek", linear, a, b, "\\left|" + linear + "\\right|-" + center + "=" + delta, high),
      makeLinearNestedCase("Drugi przypadek", linear, a, b, "\\left|" + linear + "\\right|-" + center + "=-" + delta, low),
    ],
  };
}

function makeLinearNestedCase(label, linear, a, b, start, target) {
  if (target < 0) {
    return {
      label,
      start,
      isolate: "\\left|" + linear + "\\right|=" + target,
      invalidReason: "Warto\u015b\u0107 bezwzgl\u0119dna nie mo\u017ce by\u0107 liczb\u0105 ujemn\u0105, wi\u0119c ten przypadek nie daje rozwi\u0105za\u0144.",
      solutions: [],
    };
  }
  const solveLines = (k) => {
    const shifted = k - b;
    const x = shifted / a;
    if (a === 1 || b === 0) return ["x=" + numberLatex(x)];
    return [a + "x=" + numberLatex(shifted), "x=" + numberLatex(x)];
  };
  const isZeroTarget = Math.abs(target) < TOL;
  return {
    label,
    start,
    isolate: "\\left|" + linear + "\\right|=" + target,
    branches: isZeroTarget ? [linear + "=0"] : [linear + "=" + target, linear + "=-" + target],
    solveLines: isZeroTarget ? [solveLines(0)] : [solveLines(target), solveLines(-target)],
    solutions: isZeroTarget ? [(0 - b) / a] : [(target - b) / a, (-target - b) / a],
  };
}

function compactLinearLatex(a, b) {
  const parts = [];
  if (a !== 0) {
    if (a === 1) parts.push("x");
    else if (a === -1) parts.push("-x");
    else parts.push(`${a}x`);
  }
  if (b !== 0 || parts.length === 0) {
    const sign = b > 0 && parts.length ? "+" : "";
    parts.push(`${sign}${b}`);
  }
  return parts.join("");
}

function termForRoot(root, increasing) {
  const a = increasing ? 1 : -1;
  const b = increasing ? -root : root;
  return { a, b, root, increasing, latex: compactLinearLatex(a, b) };
}

function sumPartsLatex(parts) {
  const tokens = [];
  parts.forEach((part) => {
    if (part.type === "x") {
      if (part.value === 0) return;
      const abs = Math.abs(part.value);
      const body = abs === 1 ? "x" : `${abs}x`;
      tokens.push({ sign: part.value < 0 ? "-" : "+", body });
      return;
    }
    if (part.value === 0) return;
    tokens.push({ sign: part.value < 0 ? "-" : "+", body: String(Math.abs(part.value)) });
  });
  if (!tokens.length) return "0";
  return tokens.map((token, index) => {
    if (index === 0) return token.sign === "-" ? `-${token.body}` : token.body;
    return `${token.sign}${token.body}`;
  }).join("");
}

function expandedLinearSumLatex(items) {
  const parts = [];
  items.forEach(({ term, sign, coefficient = 1 }) => {
    const multiplier = sign * coefficient;
    parts.push({ type: "x", value: multiplier * term.a });
    parts.push({ type: "const", value: multiplier * term.b });
  });
  return sumPartsLatex(parts);
}

function solveLinearCaseLatex(coef, constant, rhs, interval, value) {
  const lines = [];
  const reduced = compactLinearLatex(coef, constant);
  lines.push(`${reduced}=${rhs}`);
  if (coef === 0) {
    lines[lines.length - 1] += rhs === constant ? "" : "\\Rightarrow\\text{sprzecznosc}";
    return lines;
  }
  const shifted = rhs - constant;
  if (constant !== 0) lines.push(`${compactLinearLatex(coef, 0)}=${numberLatex(shifted)}`);
  lines.push(`x=${numberLatex(value, { mixed: true })}${interval.contains(value) ? "" : "\\quad\\text{poza przedzialem}"}`);
  return lines;
}

function denominatorOf(value) {
  if (!Number.isFinite(value)) return Infinity;
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < TOL) return 1;
  for (let d = 2; d <= 24; d += 1) {
    const n = Math.round(value * d);
    if (Math.abs(value - n / d) < TOL) return d / gcd(n, d);
  }
  return Infinity;
}

function solveLinearAgainstLinearCaseLatex(leftCoef, leftConst, rhsCoef, rhsConst, interval, value) {
  const lines = [];
  const left = compactLinearLatex(leftCoef, leftConst);
  const right = compactLinearLatex(rhsCoef, rhsConst);
  lines.push(`${left}=${right}`);
  const diffCoef = leftCoef - rhsCoef;
  const diffConst = leftConst - rhsConst;
  if (diffCoef === 0) {
    if (diffConst === 0) lines.push("0=0");
    else lines.push(`${diffConst}=0\\Rightarrow\\text{sprzecznosc}`);
    return lines;
  }
  lines.push(`${compactLinearLatex(diffCoef, diffConst)}=0`);
  const shifted = -diffConst;
  if (diffConst !== 0) lines.push(`${compactLinearLatex(diffCoef, 0)}=${numberLatex(shifted)}`);
  lines.push(`x=${numberLatex(value, { mixed: true })}${interval.contains(value) ? "" : "\\quad\\text{poza przedzialem}"}`);
  return lines;
}

const WEIGHTED_ABS_COEFFICIENTS = [-5, -4, -3, -2, 2, 3, 4, 5];
const controlledSinglePoolsCache = new Map();

function buildControlledSinglePools({ weighted = false } = {}) {
  const cacheKey = weighted ? "weighted" : "unweighted";
  if (controlledSinglePoolsCache.has(cacheKey)) {
    return controlledSinglePoolsCache.get(cacheKey);
  }

  const oneSolution = [];
  const twoSolutions = [];
  const outsideCoefs = [-3, -2, -1, 1, 2, 3];
  const absCoefs = weighted ? WEIGHTED_ABS_COEFFICIENTS : [1];
  const seen = new Set();

  for (let insideCoef = 1; insideCoef <= 3; insideCoef += 1) {
    for (let insideConst = -8; insideConst <= 8; insideConst += 1) {
      if (gcd(insideCoef, insideConst) !== 1) continue;
      const critical = -insideConst / insideCoef;
      if (Math.abs(critical) > 5 || denominatorOf(critical) > 3) continue;

      for (const absCoef of absCoefs) {
        for (const outsideCoef of outsideCoefs) {
          for (let outsideConst = -9; outsideConst <= 9; outsideConst += 1) {
            const intervals = [
              {
                label: `x<${numberLatex(critical, { mixed: true })}`,
                sign: -1,
                contains: (x) => x < critical - TOL,
              },
              {
                label: `x\\ge ${numberLatex(critical, { mixed: true })}`,
                sign: 1,
                contains: (x) => x >= critical - TOL,
              },
            ];
            const solutions = [];
            let hasIdentityCase = false;

            intervals.forEach((interval) => {
              const rhsCoef = absCoef * interval.sign * insideCoef;
              const rhsConst = absCoef * interval.sign * insideConst;
              const diffCoef = outsideCoef - rhsCoef;
              const diffConst = outsideConst - rhsConst;
              if (diffCoef === 0) {
                if (diffConst === 0) hasIdentityCase = true;
                return;
              }
              const value = -diffConst / diffCoef;
              if (interval.contains(value)) solutions.push(value);
            });

            if (hasIdentityCase) continue;
            const uniqueSolutions = uniqueSorted(solutions);
            if (uniqueSolutions.length < 1 || uniqueSolutions.length > 2) continue;
            if (
              !uniqueSolutions.every(
                (value) => Math.abs(value) <= 10 && denominatorOf(value) <= 6
              )
            ) {
              continue;
            }
            if (
              uniqueSolutions.length === 1 &&
              Math.abs(uniqueSolutions[0] - critical) < TOL
            ) {
              continue;
            }

            const key = [
              insideCoef,
              insideConst,
              absCoef,
              outsideCoef,
              outsideConst,
            ].join(":");
            if (seen.has(key)) continue;
            seen.add(key);

            const spec = {
              insideCoef,
              insideConst,
              absCoef,
              outsideCoef,
              outsideConst,
              critical,
              solutions: uniqueSolutions,
            };
            (uniqueSolutions.length === 1 ? oneSolution : twoSolutions).push(spec);
          }
        }
      }
    }
  }

  const pools = { oneSolution, twoSolutions };
  controlledSinglePoolsCache.set(cacheKey, pools);
  return pools;
}

function signedXTermLatex(coef) {
  if (coef === 1) return "+x";
  if (coef === -1) return "-x";
  return coef > 0 ? `+${coef}x` : `${coef}x`;
}

function scaledParenthesizedLatex(multiplier, latex, index = 0) {
  const magnitude = Math.abs(multiplier);
  const body = `${magnitude === 1 ? "" : magnitude}(${latex})`;
  if (multiplier < 0) return `-${body}`;
  return index === 0 ? body : `+${body}`;
}

function scaledAbsoluteLatex(coefficient, latex, index = 0) {
  const magnitude = Math.abs(coefficient);
  const body = `${magnitude === 1 ? "" : magnitude}\\left|${latex}\\right|`;
  if (coefficient < 0) return `-${body}`;
  return index === 0 ? body : `+${body}`;
}

function makeControlledSingleVariant({ weighted = false } = {}) {
  const pools = buildControlledSinglePools({ weighted });
  const preferredPool = Math.random() < 0.35 ? pools.oneSolution : pools.twoSolutions;
  const pool = preferredPool.length ? preferredPool : [...pools.oneSolution, ...pools.twoSolutions];
  const spec = pool[Math.floor(Math.random() * pool.length)];
  const {
    insideCoef,
    insideConst,
    absCoef,
    outsideCoef,
    outsideConst,
    critical,
    solutions,
  } = spec;
  const insideLatex = compactLinearLatex(insideCoef, insideConst);
  const outsideLatex = compactLinearLatex(outsideCoef, outsideConst);
  const absLatex = `\\left|${insideLatex}\\right|`;
  const scaledAbsLatex =
    absCoef === 1 ? absLatex : absCoef === -1 ? `-${absLatex}` : `${absCoef}${absLatex}`;
  const displayMode = Math.floor(Math.random() * 3);
  let equation = `${outsideLatex}=${scaledAbsLatex}`;
  if (displayMode === 1) equation = `${scaledAbsLatex}=${outsideLatex}`;
  if (displayMode === 2) {
    equation = `${scaledAbsLatex}${signedXTermLatex(-outsideCoef)}=${numberLatex(outsideConst)}`;
  }

  const intervals = [
    {
      label: `x<${numberLatex(critical, { mixed: true })}`,
      sign: -1,
      contains: (x) => x < critical - TOL,
    },
    {
      label: `x\\ge ${numberLatex(critical, { mixed: true })}`,
      sign: 1,
      contains: (x) => x >= critical - TOL,
    },
  ];
  const caseSteps = intervals.map((interval) => {
    const rhsCoef = absCoef * interval.sign * insideCoef;
    const rhsConst = absCoef * interval.sign * insideConst;
    const signedInside =
      interval.sign > 0 ? `(${insideLatex})` : `-(${insideLatex})`;
    const substituted = scaledParenthesizedLatex(
      absCoef * interval.sign,
      insideLatex
    );
    const diffCoef = outsideCoef - rhsCoef;
    const diffConst = outsideConst - rhsConst;
    const value = diffCoef === 0 ? null : -diffConst / diffCoef;
    const solveLines = solveLinearAgainstLinearCaseLatex(
      outsideCoef,
      outsideConst,
      rhsCoef,
      rhsConst,
      interval,
      value
    );
    return `${interval.label}:\\quad ${outsideLatex}=${substituted}\\Rightarrow ${solveLines.join("\\Rightarrow")}`;
  });

  return {
    id: `single-dynamic-${insideCoef}-${insideConst}-${absCoef}-${outsideCoef}-${outsideConst}-${displayMode}`,
    family: "single",
    weighted,
    absoluteCoefficients: [absCoef],
    outsideLinearCoefficient: outsideCoef,
    equation,
    answerKind: "finite",
    solutions,
    critical: [critical],
    splitLatex: insideLatex,
    intervals: intervals.map((item) => item.label),
    steps: [
      `${insideLatex}=0\\iff x=${numberLatex(critical, { mixed: true })}`,
      ...caseSteps,
      `x\\in ${setLatex(solutions, { mixed: true, sort: true })}`,
    ],
  };
}

function makeControlledDoubleLinearVariant({
  weighted = false,
  constantRight = false,
} = {}) {
  const orientations = [
    [true, true],
    [true, false],
    [false, true],
    [false, false],
  ];
  const rhsCoefs = constantRight ? [0] : [-3, -2, -1, 1, 2, 3];
  const intervalCasesFor = (leftRoot, rightRoot) => [
    {
      label: `x<${numberLatex(leftRoot, { mixed: true })}`,
      probe: leftRoot - 1,
      contains: (x) => x < leftRoot - TOL,
      closedContains: (x) => x < leftRoot - TOL,
    },
    {
      label: `${numberLatex(leftRoot, { mixed: true })}\\le x<${numberLatex(rightRoot, { mixed: true })}`,
      probe: (leftRoot + rightRoot) / 2,
      contains: (x) => x >= leftRoot - TOL && x < rightRoot - TOL,
      closedContains: (x) => x >= leftRoot - TOL && x <= rightRoot + TOL,
    },
    {
      label: `x\\ge ${numberLatex(rightRoot, { mixed: true })}`,
      probe: rightRoot + 1,
      contains: (x) => x >= rightRoot - TOL,
      closedContains: (x) => x >= rightRoot - TOL,
    },
  ];

  for (let attempt = 0; attempt < 2000; attempt += 1) {
    const leftRoot = -6 + Math.floor(Math.random() * 9);
    const rightRoot = leftRoot + 2 + Math.floor(Math.random() * Math.min(7, 6 - leftRoot));
    const [leftIncreasing, rightIncreasing] = orientations[Math.floor(Math.random() * orientations.length)];
    const rhsCoef = rhsCoefs[Math.floor(Math.random() * rhsCoefs.length)];
    const rhsConst = -8 + Math.floor(Math.random() * 17);
    const leftTerm = termForRoot(leftRoot, leftIncreasing);
    const rightTerm = termForRoot(rightRoot, rightIncreasing);
    const coefficients = weighted
      ? Math.random() < 0.5
        ? [
            2 + Math.floor(Math.random() * 4),
            -(2 + Math.floor(Math.random() * 4)),
          ]
        : [
            -(2 + Math.floor(Math.random() * 4)),
            2 + Math.floor(Math.random() * 4),
          ]
      : [1, 1];
    const intervals = intervalCasesFor(leftRoot, rightRoot);
    const rhsLatex = compactLinearLatex(rhsCoef, rhsConst);
    const caseSteps = [];
    const pointSolutions = [];
    const intervalSolutions = [];

    intervals.forEach((interval, intervalIndex) => {
      const signedTerms = [leftTerm, rightTerm].map((term, index) => ({
        term,
        sign: term.a * interval.probe + term.b >= 0 ? 1 : -1,
        coefficient: coefficients[index],
      }));
      const substituted = signedTerms
        .map(({ term, sign, coefficient }, index) =>
          scaledParenthesizedLatex(coefficient * sign, term.latex, index)
        )
        .join("");
      const expanded = expandedLinearSumLatex(signedTerms);
      const leftCoef = signedTerms.reduce(
        (sum, item) => sum + item.coefficient * item.sign * item.term.a,
        0
      );
      const leftConst = signedTerms.reduce(
        (sum, item) => sum + item.coefficient * item.sign * item.term.b,
        0
      );
      const diffCoef = leftCoef - rhsCoef;
      const diffConst = leftConst - rhsConst;
      const value = diffCoef === 0 ? null : -diffConst / diffCoef;
      if (diffCoef === 0 && diffConst === 0) intervalSolutions.push(intervalIndex);
      if (diffCoef !== 0 && interval.contains(value)) pointSolutions.push(value);
      const solveLines = solveLinearAgainstLinearCaseLatex(leftCoef, leftConst, rhsCoef, rhsConst, interval, value);
      const parts = [`${substituted}=${rhsLatex}`, `${expanded}=${rhsLatex}`, ...solveLines];
      caseSteps.push(`${interval.label}:\\quad ${parts.join("\\Rightarrow")}`);
    });

    const uniquePoints = uniqueSorted(pointSolutions);
    const nicePoints = uniquePoints.every((value) => Math.abs(value) <= 10 && denominatorOf(value) <= 6);
    if (intervalSolutions.length > 1 || !nicePoints) continue;

    const base = {
      id: `double-linear-dynamic-${leftRoot}-${rightRoot}-${leftIncreasing ? "u" : "d"}${rightIncreasing ? "u" : "d"}-${coefficients.join("_")}-${rhsCoef}-${rhsConst}`,
      family: "double-linear",
      weighted,
      absoluteCoefficients: coefficients,
      outsideLinearCoefficient: rhsCoef,
      equation: `${scaledAbsoluteLatex(
        coefficients[0],
        leftTerm.latex,
        0
      )}${scaledAbsoluteLatex(
        coefficients[1],
        rightTerm.latex,
        1
      )}=${rhsLatex}`,
      critical: [leftRoot, rightRoot],
      splitLatex: `${leftTerm.latex},\\;${rightTerm.latex}`,
      intervals: intervals.map((item) => item.label),
      steps: [
        `Punkty krytyczne: x=${numberLatex(leftRoot, { mixed: true })}\\;\\text{oraz}\\;x=${numberLatex(rightRoot, { mixed: true })}`,
        ...caseSteps,
      ],
    };

    if (intervalSolutions.length === 1) {
      const intervalIndex = intervalSolutions[0];
      if (intervalIndex !== 1) continue;
      const interval = { left: leftRoot, right: rightRoot, leftClosed: true, rightClosed: true };
      const outsidePoints = uniquePoints.filter((value) => !intervals[1].closedContains(value));
      if (outsidePoints.length) continue;
      return {
        ...base,
        answerKind: "interval",
        interval,
        steps: [...base.steps, `x\\in[${numberLatex(leftRoot, { mixed: true })},${numberLatex(rightRoot, { mixed: true })}]`],
      };
    }

    if (uniquePoints.length >= 1 && uniquePoints.length <= 3) {
      return {
        ...base,
        answerKind: "finite",
        solutions: uniquePoints,
        steps: [...base.steps, `x\\in ${setLatex(uniquePoints, { mixed: true, sort: true })}`],
      };
    }
  }

  if (weighted || constantRight) {
    return makeControlledDoubleLinearVariant({ weighted, constantRight });
  }

  return {
    id: "double-linear-fallback",
    family: "double-linear",
    equation: "\\left|x+3\\right|+\\left|2-x\\right|=x+5",
    answerKind: "finite",
    solutions: [0, 4],
    critical: [-3, 2],
    splitLatex: "x+3,\\;2-x",
    intervals: ["x<-3", "-3\\le x<2", "x\\ge 2"],
    steps: [
      "Punkty krytyczne: x=-3\\;\\text{oraz}\\;x=2",
      "x<-3:\\quad -(x+3)+(2-x)=x+5\\Rightarrow -x-3+2-x=x+5\\Rightarrow -2x-1=x+5\\Rightarrow -3x=6\\Rightarrow x=-2\\quad\\text{poza przedzialem}",
      "-3\\le x<2:\\quad (x+3)+(2-x)=x+5\\Rightarrow x+3+2-x=x+5\\Rightarrow 5=x+5\\Rightarrow x=0",
      "x\\ge 2:\\quad (x+3)-(2-x)=x+5\\Rightarrow x+3-2+x=x+5\\Rightarrow 2x+1=x+5\\Rightarrow x=4",
      "x\\in\\{0,4\\}",
    ],
  };
}
function makeControlledDoubleVariant() {
  const specs = [];
  const orientations = [
    [true, true],
    [true, false],
    [false, true],
    [false, false],
  ];
  for (let leftRoot = -6; leftRoot <= 3; leftRoot += 1) {
    for (let rightRoot = leftRoot + 2; rightRoot <= Math.min(6, leftRoot + 8); rightRoot += 1) {
      orientations.forEach(([leftIncreasing, rightIncreasing]) => {
        specs.push({ leftRoot, rightRoot, leftIncreasing, rightIncreasing, kind: "interval" });
        specs.push({ leftRoot, rightRoot, leftIncreasing, rightIncreasing, kind: "finite" });
      });
    }
  }
  const spec = specs[Math.floor(Math.random() * specs.length)];
  const leftTerm = termForRoot(spec.leftRoot, spec.leftIncreasing);
  const rightTerm = termForRoot(spec.rightRoot, spec.rightIncreasing);
  const distance = spec.rightRoot - spec.leftRoot;
  const rhs = spec.kind === "interval" ? distance : distance + 2;
  const intervalCases = [
    {
      label: `x<${numberLatex(spec.leftRoot, { mixed: true })}`,
      probe: spec.leftRoot - 1,
      contains: (x) => x < spec.leftRoot - TOL,
    },
    {
      label: `${numberLatex(spec.leftRoot, { mixed: true })}\\le x<${numberLatex(spec.rightRoot, { mixed: true })}`,
      probe: (spec.leftRoot + spec.rightRoot) / 2,
      contains: (x) => x >= spec.leftRoot - TOL && x < spec.rightRoot - TOL,
    },
    {
      label: `x\\ge ${numberLatex(spec.rightRoot, { mixed: true })}`,
      probe: spec.rightRoot + 1,
      contains: (x) => x >= spec.rightRoot - TOL,
    },
  ];
  const caseSteps = intervalCases.map((interval) => {
    const signedTerms = [leftTerm, rightTerm].map((term) => ({
      term,
      sign: term.a * interval.probe + term.b >= 0 ? 1 : -1,
    }));
    const substituted = signedTerms.map(({ term, sign }, index) => {
      const prefix = sign > 0 ? (index === 0 ? "" : "+") : "-";
      return `${prefix}(${term.latex})`;
    }).join("");
    const expanded = expandedLinearSumLatex(signedTerms);
    const coef = signedTerms.reduce((sum, item) => sum + item.sign * item.term.a, 0);
    const constant = signedTerms.reduce((sum, item) => sum + item.sign * item.term.b, 0);
    const value = coef === 0 ? null : (rhs - constant) / coef;
    const solveLines = solveLinearCaseLatex(coef, constant, rhs, interval, value);
    const parts = [`${substituted}=${rhs}`, `${expanded}=${rhs}`, ...solveLines];
    return `${interval.label}:\\quad ${parts.join("\\Rightarrow")}`;
  });
  const equation = `\\left|${leftTerm.latex}\\right|+\\left|${rightTerm.latex}\\right|=${rhs}`;
  const id = `double-dynamic-${spec.leftRoot}-${spec.rightRoot}-${spec.leftIncreasing ? "u" : "d"}${spec.rightIncreasing ? "u" : "d"}-${spec.kind}`;
  if (spec.kind === "interval") {
    return {
      id,
      family: "double",
      equation,
      answerKind: "interval",
      interval: { left: spec.leftRoot, right: spec.rightRoot, leftClosed: true, rightClosed: true },
      critical: [spec.leftRoot, spec.rightRoot],
      splitLatex: `${leftTerm.latex},\\;${rightTerm.latex}`,
      intervals: intervalCases.map((item) => item.label),
      steps: [
        `Punkty krytyczne: x=${numberLatex(spec.leftRoot, { mixed: true })}\\;\\text{oraz}\\;x=${numberLatex(spec.rightRoot, { mixed: true })}`,
        ...caseSteps,
        `x\\in[${numberLatex(spec.leftRoot, { mixed: true })},${numberLatex(spec.rightRoot, { mixed: true })}]`,
      ],
    };
  }
  const solutions = uniqueSorted([spec.leftRoot - 1, spec.rightRoot + 1]);
  return {
    id,
    family: "double",
    equation,
    answerKind: "finite",
    solutions,
    critical: [spec.leftRoot, spec.rightRoot],
    splitLatex: `${leftTerm.latex},\\;${rightTerm.latex}`,
    intervals: intervalCases.map((item) => item.label),
    steps: [
      `Punkty krytyczne: x=${numberLatex(spec.leftRoot, { mixed: true })}\\;\\text{oraz}\\;x=${numberLatex(spec.rightRoot, { mixed: true })}`,
      ...caseSteps,
      `x\\in ${setLatex(solutions, { mixed: true, sort: true })}`,
    ],
  };
}
const VARIANTS = [
  {
    id: "nested-1",
    family: "nested",
    slot: "nested-simple",
    equation: "\\left|\\left|x\\right|-6\\right|=4",
    answerKind: "finite",
    solutions: [-10, -2, 2, 10],
    critical: [0, -6, 6],
    steps: [
      "\\left|x\\right|-6=4\\quad\\text{lub}\\quad\\left|x\\right|-6=-4",
      "\\left|x\\right|=10\\quad\\text{lub}\\quad\\left|x\\right|=2",
      "x\\in\\{-10,-2,2,10\\}",
    ],
  },
  {
    id: "nested-2",
    family: "nested",
    slot: "nested-simple",
    equation: "\\left|\\left|x\\right|-2\\right|=1",
    answerKind: "finite",
    solutions: [-3, -1, 1, 3],
    critical: [0, -2, 2],
    steps: [
      "\\left|x\\right|-2=1\\quad\\text{lub}\\quad\\left|x\\right|-2=-1",
      "\\left|x\\right|=3\\quad\\text{lub}\\quad\\left|x\\right|=1",
      "x\\in\\{-3,-1,1,3\\}",
    ],
  },
  {
    id: "nested-3",
    family: "nested",
    slot: "nested-linear",
    equation: "\\left|\\left|2x+5\\right|-4\\right|=3",
    answerKind: "finite",
    solutions: [-6, -3, -2, 1],
    critical: [-2.5, -6, 1, -3, -2],
    steps: [
      "\\left|2x+5\\right|-4=3\\quad\\text{lub}\\quad\\left|2x+5\\right|-4=-3",
      "\\left|2x+5\\right|=7\\quad\\text{lub}\\quad\\left|2x+5\\right|=1",
      "2x+5=7,\\;-7,\\;1,\\;-1",
      "x\\in\\{-6,-3,-2,1\\}",
    ],
  },
  {
    id: "nested-4",
    family: "nested",
    slot: "nested-linear",
    equation: "\\left|7-\\left|2x-5\\right|\\right|=6",
    answerKind: "finite",
    solutions: [-4, 2, 3, 9],
    critical: [2.5, -4, 2, 3, 9],
    steps: [
      "7-\\left|2x-5\\right|=6\\quad\\text{lub}\\quad 7-\\left|2x-5\\right|=-6",
      "\\left|2x-5\\right|=1\\quad\\text{lub}\\quad\\left|2x-5\\right|=13",
      "2x-5=1,\\;-1,\\;13,\\;-13",
      "x\\in\\{-4,2,3,9\\}",
    ],
  },

  {
    id: "nested-5",
    family: "nested",
    slot: "nested-linear",
    equation: "\\left|\\left|2x+1\\right|-3\\right|=5",
    answerKind: "finite",
    solutions: [-4.5, 3.5],
    critical: [-0.5, -4.5, 3.5],
    steps: [
      "\\left|2x+1\\right|-3=5\\quad\\text{lub}\\quad\\left|2x+1\\right|-3=-5",
      "\\left|2x+1\\right|=8\\quad\\text{lub}\\quad\\left|2x+1\\right|=-2",
      "x\\in\\{-\\dfrac{9}{2},\\dfrac{7}{2}\\}",
    ],
  },
  {
    id: "nested-6",
    family: "nested",
    slot: "nested-linear",
    equation: "\\left|5-\\left|3x-2\\right|\\right|=7",
    answerKind: "finite",
    solutions: [-10 / 3, 14 / 3],
    critical: [2 / 3, -10 / 3, 14 / 3],
    steps: [
      "5-\\left|3x-2\\right|=7\\quad\\text{lub}\\quad 5-\\left|3x-2\\right|=-7",
      "\\left|3x-2\\right|=-2\\quad\\text{lub}\\quad\\left|3x-2\\right|=12",
      "x\\in\\{-\\dfrac{10}{3},\\dfrac{14}{3}\\}",
    ],
  },
  {
    id: "single-1",
    family: "single",
    equation: "\\left|2-x\\right|=x-1",
    answerKind: "finite",
    solutions: [1.5],
    critical: [2],
    splitLatex: "2-x",
    intervals: ["x\\le 2", "x>2"],
    steps: [
      "2-x\\ge 0\\iff x\\le 2,\\quad 2-x<0\\iff x>2",
      "x\\le 2:\\quad 2-x=x-1\\Rightarrow 3=2x\\Rightarrow x=\\dfrac{3}{2}",
      "x>2:\\quad x-2=x-1\\Rightarrow -2=-1\\Rightarrow\\text{sprzecznosc}",
      "x=\\dfrac{3}{2}",
    ],
  },
  {
    id: "single-2",
    family: "single",
    equation: "\\left|3x-8\\right|+2=2x",
    answerKind: "finite",
    solutions: [2, 6],
    critical: [8 / 3],
    splitLatex: "3x-8",
    intervals: ["x<\\dfrac{8}{3}", "x\\ge\\dfrac{8}{3}"],
    steps: [
      "3x-8<0\\iff x<\\dfrac{8}{3},\\quad 3x-8\\ge 0\\iff x\\ge\\dfrac{8}{3}",
      "x<\\dfrac{8}{3}:\\quad -(3x-8)+2=2x\\Rightarrow -3x+10=2x\\Rightarrow 10=5x\\Rightarrow x=2",
      "x\\ge\\dfrac{8}{3}:\\quad 3x-8+2=2x\\Rightarrow 3x-6=2x\\Rightarrow x=6",
      "x\\in\\{2,6\\}",
    ],
  },
  {
    id: "single-3",
    family: "single",
    equation: "6+x=2\\left|x+3\\right|",
    answerKind: "finite",
    solutions: [-4, 0],
    critical: [-3],
    splitLatex: "x+3",
    intervals: ["x<-3", "x\\ge -3"],
    steps: [
      "x+3<0\\iff x<-3,\\quad x+3\\ge 0\\iff x\\ge -3",
      "x<-3:\\quad 6+x=2(-x-3)\\Rightarrow 6+x=-2x-6\\Rightarrow 3x=-12\\Rightarrow x=-4",
      "x\\ge -3:\\quad 6+x=2(x+3)\\Rightarrow 6+x=2x+6\\Rightarrow x=0",
      "x\\in\\{-4,0\\}",
    ],
  },
  {
    id: "single-4",
    family: "single",
    equation: "\\left|3-2x\\right|+x=3",
    answerKind: "finite",
    solutions: [0, 2],
    critical: [1.5],
    splitLatex: "3-2x",
    intervals: ["x\\le\\dfrac{3}{2}", "x>\\dfrac{3}{2}"],
    steps: [
      "3-2x\\ge 0\\iff x\\le\\dfrac{3}{2},\\quad 3-2x<0\\iff x>\\dfrac{3}{2}",
      "x\\le\\dfrac{3}{2}:\\quad 3-2x+x=3\\Rightarrow 3-x=3\\Rightarrow x=0",
      "x>\\dfrac{3}{2}:\\quad 2x-3+x=3\\Rightarrow 3x-3=3\\Rightarrow 3x=6\\Rightarrow x=2",
      "x\\in\\{0,2\\}",
    ],
  },
  {
    id: "double-1",
    family: "double",
    equation: "\\left|x-1\\right|+\\left|x+3\\right|=4",
    answerKind: "interval",
    interval: { left: -3, right: 1, leftClosed: true, rightClosed: true },
    critical: [-3, 1],
    splitLatex: "x+3,\\;x-1",
    intervals: ["x<-3", "-3\\le x<1", "x\\ge 1"],
    steps: [
      "Punkty krytyczne: x=-3\\;\\text{oraz}\\;x=1",
      "x<-3:\\quad -(x-1)-(x+3)=4\\Rightarrow -x+1-x-3=4\\Rightarrow -2x-2=4\\Rightarrow -2x=6\\Rightarrow x=-3\\quad\\text{poza przedzialem}",
      "-3\\le x<1:\\quad -(x-1)+(x+3)=4\\Rightarrow -x+1+x+3=4\\Rightarrow 4=4",
      "x\\ge 1:\\quad (x-1)+(x+3)=4\\Rightarrow 2x+2=4\\Rightarrow 2x=2\\Rightarrow x=1",
      "x\\in[-3,1]",
    ],
  },
  {
    id: "double-2",
    family: "double",
    equation: "\\left|2x+3\\right|-\\left|x-1\\right|=4",
    answerKind: "finite",
    solutions: [-8, 2 / 3],
    critical: [-1.5, 1],
    splitLatex: "2x+3,\\;x-1",
    intervals: ["x<-\\dfrac{3}{2}", "-\\dfrac{3}{2}\\le x<1", "x\\ge 1"],
    steps: [
      "Punkty krytyczne: x=-\\dfrac{3}{2}\\;\\text{oraz}\\;x=1",
      "x<-\\dfrac{3}{2}:\\quad -(2x+3)-(-(x-1))=4\\Rightarrow -2x-3+x-1=4\\Rightarrow -x-4=4\\Rightarrow -x=8\\Rightarrow x=-8",
      "-\\dfrac{3}{2}\\le x<1:\\quad (2x+3)-(-(x-1))=4\\Rightarrow 2x+3+x-1=4\\Rightarrow 3x+2=4\\Rightarrow 3x=2\\Rightarrow x=\\dfrac{2}{3}",
      "x\\ge 1:\\quad (2x+3)-(x-1)=4\\Rightarrow 2x+3-x+1=4\\Rightarrow x+4=4\\Rightarrow x=0\\quad\\text{poza przedzialem}",
      "x\\in\\{-8,\\dfrac{2}{3}\\}",
    ],
  },
  {
    id: "double-3",
    family: "double",
    equation: "\\left|x+2\\right|-\\left|2x-7\\right|=1-x",
    answerKind: "finite",
    solutions: [1.5],
    critical: [-2, 3.5],
    splitLatex: "x+2,\\;2x-7",
    intervals: ["x<-2", "-2\\le x<\\dfrac{7}{2}", "x\\ge\\dfrac{7}{2}"],
    steps: [
      "Punkty krytyczne: x=-2\\;\\text{oraz}\\;x=\\dfrac{7}{2}",
      "x<-2:\\quad -(x+2)-(-(2x-7))=1-x\\Rightarrow -x-2+2x-7=1-x\\Rightarrow x-9=1-x\\Rightarrow 2x=10\\Rightarrow x=5\\quad\\text{poza przedzialem}",
      "-2\\le x<\\dfrac{7}{2}:\\quad (x+2)-(-(2x-7))=1-x\\Rightarrow x+2+2x-7=1-x\\Rightarrow 3x-5=1-x\\Rightarrow 4x=6\\Rightarrow x=\\dfrac{3}{2}",
      "x\\ge\\dfrac{7}{2}:\\quad (x+2)-(2x-7)=1-x\\Rightarrow x+2-2x+7=1-x\\Rightarrow -x+9=1-x\\Rightarrow 9=1\\Rightarrow\\text{sprzecznosc}",
      "x=\\dfrac{3}{2}",
    ],
  },
  {
    id: "double-4",
    family: "double",
    equation: "\\left|x+1\\right|+x=6-\\left|3-x\\right|",
    answerKind: "finite",
    solutions: [-4, 2],
    critical: [-1, 3],
    splitLatex: "x+1,\\;3-x",
    intervals: ["x<-1", "-1\\le x<3", "x\\ge 3"],
    steps: [
      "Punkty krytyczne: x=-1\\;\\text{oraz}\\;x=3",
      "x<-1:\\quad -(x+1)+x=6-(3-x)\\Rightarrow -x-1+x=3+x\\Rightarrow -1=3+x\\Rightarrow x=-4",
      "-1\\le x<3:\\quad (x+1)+x=6-(3-x)\\Rightarrow 2x+1=3+x\\Rightarrow x=2",
      "x\\ge 3:\\quad (x+1)+x=6-(x-3)\\Rightarrow 2x+1=9-x\\Rightarrow 3x=8\\Rightarrow x=\\dfrac{8}{3}\\quad\\text{poza przedzialem}",
      "x\\in\\{-4,2\\}",
    ],
  },
  {
    id: "double-5",
    family: "double",
    equation: "\\left|x+2\\right|+\\left|x-2\\right|=6",
    answerKind: "finite",
    solutions: [-3, 3],
    critical: [-2, 2],
    splitLatex: "x+2,\\;x-2",
    intervals: ["x<-2", "-2\\le x<2", "x\\ge 2"],
    steps: [
      "Punkty krytyczne: x=-2\\;\\text{oraz}\\;x=2",
      "x<-2:\\quad -(x+2)-(x-2)=6\\Rightarrow -x-2-x+2=6\\Rightarrow -2x=6\\Rightarrow x=-3",
      "-2\\le x<2:\\quad (x+2)-(x-2)=6\\Rightarrow x+2-x+2=6\\Rightarrow 4=6\\Rightarrow\\text{sprzecznosc}",
      "x\\ge 2:\\quad (x+2)+(x-2)=6\\Rightarrow 2x=6\\Rightarrow x=3",
      "x\\in\\{-3,3\\}",
    ],
  },
  {
    id: "double-6",
    family: "double",
    equation: "\\left|x+2\\right|+\\left|x-2\\right|=4",
    answerKind: "interval",
    interval: { left: -2, right: 2, leftClosed: true, rightClosed: true },
    critical: [-2, 2],
    splitLatex: "x+2,\\;x-2",
    intervals: ["x<-2", "-2\\le x<2", "x\\ge 2"],
    steps: [
      "Punkty krytyczne: x=-2\\;\\text{oraz}\\;x=2",
      "x<-2:\\quad -(x+2)-(x-2)=4\\Rightarrow -2x=4\\Rightarrow x=-2\\quad\\text{poza przedzialem}",
      "-2\\le x<2:\\quad (x+2)-(x-2)=4\\Rightarrow x+2-x+2=4\\Rightarrow 4=4",
      "x\\ge 2:\\quad (x+2)+(x-2)=4\\Rightarrow 2x=4\\Rightarrow x=2",
      "x\\in[-2,2]",
    ],
  },
  {
    id: "double-7",
    family: "double",
    equation: "\\left|3-x\\right|+\\left|1-x\\right|=4",
    answerKind: "finite",
    solutions: [0, 4],
    critical: [1, 3],
    splitLatex: "3-x,\\;1-x",
    intervals: ["x<1", "1\\le x<3", "x\\ge 3"],
    steps: [
      "Punkty krytyczne: x=1\\;\\text{oraz}\\;x=3",
      "x<1:\\quad (3-x)+(1-x)=4\\Rightarrow 4-2x=4\\Rightarrow -2x=0\\Rightarrow x=0",
      "1\\le x<3:\\quad (3-x)-(1-x)=4\\Rightarrow 3-x-1+x=4\\Rightarrow 2=4\\Rightarrow\\text{sprzecznosc}",
      "x\\ge 3:\\quad -(3-x)-(1-x)=4\\Rightarrow x-3+x-1=4\\Rightarrow 2x-4=4\\Rightarrow 2x=8\\Rightarrow x=4",
      "x\\in\\{0,4\\}",
    ],
  },
  {
    id: "double-8",
    family: "double",
    equation: "\\left|3-x\\right|+\\left|x+1\\right|=4",
    answerKind: "interval",
    interval: { left: -1, right: 3, leftClosed: true, rightClosed: true },
    critical: [-1, 3],
    splitLatex: "x+1,\\;3-x",
    intervals: ["x<-1", "-1\\le x<3", "x\\ge 3"],
    steps: [
      "Punkty krytyczne: x=-1\\;\\text{oraz}\\;x=3",
      "x<-1:\\quad (3-x)-(x+1)=4\\Rightarrow 2-2x=4\\Rightarrow -2x=2\\Rightarrow x=-1\\quad\\text{poza przedzialem}",
      "-1\\le x<3:\\quad (3-x)+(x+1)=4\\Rightarrow 4=4",
      "x\\ge 3:\\quad -(3-x)+(x+1)=4\\Rightarrow x-3+x+1=4\\Rightarrow 2x-2=4\\Rightarrow 2x=6\\Rightarrow x=3",
      "x\\in[-1,3]",
    ],
  },
  {
    id: "double-9",
    family: "double",
    equation: "\\left|x+2\\right|-\\left|4-x\\right|=2",
    answerKind: "finite",
    solutions: [2],
    critical: [-2, 4],
    splitLatex: "x+2,\\;4-x",
    intervals: ["x<-2", "-2\\le x<4", "x\\ge 4"],
    steps: [
      "Punkty krytyczne: x=-2\\;\\text{oraz}\\;x=4",
      "x<-2:\\quad -(x+2)-(4-x)=2\\Rightarrow -x-2-4+x=2\\Rightarrow -6=2\\Rightarrow\\text{sprzecznosc}",
      "-2\\le x<4:\\quad (x+2)-(4-x)=2\\Rightarrow x+2-4+x=2\\Rightarrow 2x-2=2\\Rightarrow 2x=4\\Rightarrow x=2",
      "x\\ge 4:\\quad (x+2)-(-(4-x))=2\\Rightarrow x+2+x-4=2\\Rightarrow 2x-2=2\\Rightarrow 2x=4\\Rightarrow x=2\\quad\\text{poza przedzialem}",
      "x=2",
    ],
  },
  {
    id: "double-10",
    family: "double",
    equation: "\\left|x+1\\right|+\\left|4-x\\right|=7",
    answerKind: "finite",
    solutions: [-2, 5],
    critical: [-1, 4],
    splitLatex: "x+1,\\;4-x",
    intervals: ["x<-1", "-1\\le x<4", "x\\ge 4"],
    steps: [
      "Punkty krytyczne: x=-1\\;\\text{oraz}\\;x=4",
      "x<-1:\\quad -(x+1)+(4-x)=7\\Rightarrow -x-1+4-x=7\\Rightarrow 3-2x=7\\Rightarrow -2x=4\\Rightarrow x=-2",
      "-1\\le x<4:\\quad (x+1)+(4-x)=7\\Rightarrow 5=7\\Rightarrow\\text{sprzecznosc}",
      "x\\ge 4:\\quad (x+1)-(4-x)=7\\Rightarrow x+1-4+x=7\\Rightarrow 2x-3=7\\Rightarrow 2x=10\\Rightarrow x=5",
      "x\\in\\{-2,5\\}",
    ],
  },
  {
    id: "double-11",
    family: "double",
    equation: "\\left|2-x\\right|-\\left|x+2\\right|=2",
    answerKind: "finite",
    solutions: [-1],
    critical: [-2, 2],
    splitLatex: "x+2,\\;2-x",
    intervals: ["x<-2", "-2\\le x<2", "x\\ge 2"],
    steps: [
      "Punkty krytyczne: x=-2\\;\\text{oraz}\\;x=2",
      "x<-2:\\quad (2-x)-(-(x+2))=2\\Rightarrow 2-x+x+2=2\\Rightarrow 4=2\\Rightarrow\\text{sprzecznosc}",
      "-2\\le x<2:\\quad (2-x)-(x+2)=2\\Rightarrow 2-x-x-2=2\\Rightarrow -2x=2\\Rightarrow x=-1",
      "x\\ge 2:\\quad -(2-x)-(x+2)=2\\Rightarrow x-2-x-2=2\\Rightarrow -4=2\\Rightarrow\\text{sprzecznosc}",
      "x=-1",
    ],
  },
  {
    id: "double-12",
    family: "double",
    equation: "\\left|5-x\\right|+\\left|2-x\\right|=5",
    answerKind: "finite",
    solutions: [1, 6],
    critical: [2, 5],
    splitLatex: "5-x,\\;2-x",
    intervals: ["x<2", "2\\le x<5", "x\\ge 5"],
    steps: [
      "Punkty krytyczne: x=2\\;\\text{oraz}\\;x=5",
      "x<2:\\quad (5-x)+(2-x)=5\\Rightarrow 7-2x=5\\Rightarrow -2x=-2\\Rightarrow x=1",
      "2\\le x<5:\\quad (5-x)-(2-x)=5\\Rightarrow 3=5\\Rightarrow\\text{sprzecznosc}",
      "x\\ge 5:\\quad -(5-x)-(2-x)=5\\Rightarrow x-5+x-2=5\\Rightarrow 2x-7=5\\Rightarrow 2x=12\\Rightarrow x=6",
      "x\\in\\{1,6\\}",
    ],
  },
  {
    id: "double-13",
    family: "double",
    equation: "\\left|x+3\\right|+\\left|x-1\\right|=6",
    answerKind: "finite",
    solutions: [-4, 2],
    critical: [-3, 1],
    splitLatex: "x+3,\\;x-1",
    intervals: ["x<-3", "-3\\le x<1", "x\\ge 1"],
    steps: [
      "Punkty krytyczne: x=-3\\;\\text{oraz}\\;x=1",
      "x<-3:\\quad -(x+3)-(x-1)=6\\Rightarrow -x-3-x+1=6\\Rightarrow -2x-2=6\\Rightarrow -2x=8\\Rightarrow x=-4",
      "-3\\le x<1:\\quad (x+3)-(x-1)=6\\Rightarrow x+3-x+1=6\\Rightarrow 4=6\\Rightarrow\\text{sprzecznosc}",
      "x\\ge 1:\\quad (x+3)+(x-1)=6\\Rightarrow 2x+2=6\\Rightarrow 2x=4\\Rightarrow x=2",
      "x\\in\\{-4,2\\}",
    ],
  },
  {
    id: "double-14",
    family: "double",
    equation: "\\left|x+3\\right|+\\left|x-1\\right|=4",
    answerKind: "interval",
    interval: { left: -3, right: 1, leftClosed: true, rightClosed: true },
    critical: [-3, 1],
    splitLatex: "x+3,\\;x-1",
    intervals: ["x<-3", "-3\\le x<1", "x\\ge 1"],
    steps: [
      "Punkty krytyczne: x=-3\\;\\text{oraz}\\;x=1",
      "x<-3:\\quad -(x+3)-(x-1)=4\\Rightarrow -2x-2=4\\Rightarrow -2x=6\\Rightarrow x=-3\\quad\\text{poza przedzialem}",
      "-3\\le x<1:\\quad (x+3)-(x-1)=4\\Rightarrow x+3-x+1=4\\Rightarrow 4=4",
      "x\\ge 1:\\quad (x+3)+(x-1)=4\\Rightarrow 2x+2=4\\Rightarrow x=1",
      "x\\in[-3,1]",
    ],
  },
  {
    id: "double-15",
    family: "double",
    equation: "\\left|4-x\\right|+\\left|2-x\\right|=4",
    answerKind: "finite",
    solutions: [1, 5],
    critical: [2, 4],
    splitLatex: "4-x,\\;2-x",
    intervals: ["x<2", "2\\le x<4", "x\\ge 4"],
    steps: [
      "Punkty krytyczne: x=2\\;\\text{oraz}\\;x=4",
      "x<2:\\quad (4-x)+(2-x)=4\\Rightarrow 6-2x=4\\Rightarrow -2x=-2\\Rightarrow x=1",
      "2\\le x<4:\\quad (4-x)-(2-x)=4\\Rightarrow 2=4\\Rightarrow\\text{sprzecznosc}",
      "x\\ge 4:\\quad -(4-x)-(2-x)=4\\Rightarrow x-4+x-2=4\\Rightarrow 2x-6=4\\Rightarrow 2x=10\\Rightarrow x=5",
      "x\\in\\{1,5\\}",
    ],
  },
  {
    id: "double-16",
    family: "double",
    equation: "\\left|4-x\\right|+\\left|2-x\\right|=2",
    answerKind: "interval",
    interval: { left: 2, right: 4, leftClosed: true, rightClosed: true },
    critical: [2, 4],
    splitLatex: "4-x,\\;2-x",
    intervals: ["x<2", "2\\le x<4", "x\\ge 4"],
    steps: [
      "Punkty krytyczne: x=2\\;\\text{oraz}\\;x=4",
      "x<2:\\quad (4-x)+(2-x)=2\\Rightarrow 6-2x=2\\Rightarrow -2x=-4\\Rightarrow x=2\\quad\\text{poza przedzialem}",
      "2\\le x<4:\\quad (4-x)-(2-x)=2\\Rightarrow 2=2",
      "x\\ge 4:\\quad -(4-x)-(2-x)=2\\Rightarrow 2x-6=2\\Rightarrow x=4",
      "x\\in[2,4]",
    ],
  },
  {
    id: "double-17",
    family: "double",
    equation: "\\left|x+4\\right|+\\left|5-x\\right|=11",
    answerKind: "finite",
    solutions: [-5, 6],
    critical: [-4, 5],
    splitLatex: "x+4,\\;5-x",
    intervals: ["x<-4", "-4\\le x<5", "x\\ge 5"],
    steps: [
      "Punkty krytyczne: x=-4\\;\\text{oraz}\\;x=5",
      "x<-4:\\quad -(x+4)+(5-x)=11\\Rightarrow -x-4+5-x=11\\Rightarrow 1-2x=11\\Rightarrow -2x=10\\Rightarrow x=-5",
      "-4\\le x<5:\\quad (x+4)+(5-x)=11\\Rightarrow 9=11\\Rightarrow\\text{sprzecznosc}",
      "x\\ge 5:\\quad (x+4)-(5-x)=11\\Rightarrow x+4-5+x=11\\Rightarrow 2x-1=11\\Rightarrow 2x=12\\Rightarrow x=6",
      "x\\in\\{-5,6\\}",
    ],
  },
  {
    id: "double-18",
    family: "double",
    equation: "\\left|x+4\\right|+\\left|5-x\\right|=9",
    answerKind: "interval",
    interval: { left: -4, right: 5, leftClosed: true, rightClosed: true },
    critical: [-4, 5],
    splitLatex: "x+4,\\;5-x",
    intervals: ["x<-4", "-4\\le x<5", "x\\ge 5"],
    steps: [
      "Punkty krytyczne: x=-4\\;\\text{oraz}\\;x=5",
      "x<-4:\\quad -(x+4)+(5-x)=9\\Rightarrow 1-2x=9\\Rightarrow -2x=8\\Rightarrow x=-4\\quad\\text{poza przedzialem}",
      "-4\\le x<5:\\quad (x+4)+(5-x)=9\\Rightarrow 9=9",
      "x\\ge 5:\\quad (x+4)-(5-x)=9\\Rightarrow 2x-1=9\\Rightarrow x=5",
      "x\\in[-4,5]",
    ],
  },
  {
    id: "double-19",
    family: "double",
    equation: "\\left|x+3\\right|-\\left|4-x\\right|=1",
    answerKind: "finite",
    solutions: [1],
    critical: [-3, 4],
    splitLatex: "x+3,\\;4-x",
    intervals: ["x<-3", "-3\\le x<4", "x\\ge 4"],
    steps: [
      "Punkty krytyczne: x=-3\\;\\text{oraz}\\;x=4",
      "x<-3:\\quad -(x+3)-(4-x)=1\\Rightarrow -x-3-4+x=1\\Rightarrow -7=1\\Rightarrow\\text{sprzecznosc}",
      "-3\\le x<4:\\quad (x+3)-(4-x)=1\\Rightarrow x+3-4+x=1\\Rightarrow 2x-1=1\\Rightarrow 2x=2\\Rightarrow x=1",
      "x\\ge 4:\\quad (x+3)-(-(4-x))=1\\Rightarrow x+3+x-4=1\\Rightarrow 2x-1=1\\Rightarrow x=1\\quad\\text{poza przedzialem}",
      "x=1",
    ],
  },
  {
    id: "double-20",
    family: "double",
    equation: "\\left|5-x\\right|-\\left|x+1\\right|=2",
    answerKind: "finite",
    solutions: [1],
    critical: [-1, 5],
    splitLatex: "x+1,\\;5-x",
    intervals: ["x<-1", "-1\\le x<5", "x\\ge 5"],
    steps: [
      "Punkty krytyczne: x=-1\\;\\text{oraz}\\;x=5",
      "x<-1:\\quad (5-x)-(-(x+1))=2\\Rightarrow 5-x+x+1=2\\Rightarrow 6=2\\Rightarrow\\text{sprzecznosc}",
      "-1\\le x<5:\\quad (5-x)-(x+1)=2\\Rightarrow 5-x-x-1=2\\Rightarrow 4-2x=2\\Rightarrow -2x=-2\\Rightarrow x=1",
      "x\\ge 5:\\quad -(5-x)-(x+1)=2\\Rightarrow x-5-x-1=2\\Rightarrow -6=2\\Rightarrow\\text{sprzecznosc}",
      "x=1",
    ],
  },
  {
    id: "double-21",
    family: "double",
    equation: "\\left|x+5\\right|+\\left|2-x\\right|=7",
    answerKind: "interval",
    interval: { left: -5, right: 2, leftClosed: true, rightClosed: true },
    critical: [-5, 2],
    splitLatex: "x+5,\\;2-x",
    intervals: ["x<-5", "-5\\le x<2", "x\\ge 2"],
    steps: [
      "Punkty krytyczne: x=-5\\;\\text{oraz}\\;x=2",
      "x<-5:\\quad -(x+5)+(2-x)=7\\Rightarrow -x-5+2-x=7\\Rightarrow -2x-3=7\\Rightarrow -2x=10\\Rightarrow x=-5\\quad\\text{poza przedzialem}",
      "-5\\le x<2:\\quad (x+5)+(2-x)=7\\Rightarrow 7=7",
      "x\\ge 2:\\quad (x+5)-(2-x)=7\\Rightarrow x+5-2+x=7\\Rightarrow 2x+3=7\\Rightarrow x=2",
      "x\\in[-5,2]",
    ],
  },
  {
    id: "double-22",
    family: "double",
    equation: "\\left|x-4\\right|+\\left|x+2\\right|=6",
    answerKind: "interval",
    interval: { left: -2, right: 4, leftClosed: true, rightClosed: true },
    critical: [-2, 4],
    splitLatex: "x+2,\\;x-4",
    intervals: ["x<-2", "-2\\le x<4", "x\\ge 4"],
    steps: [
      "Punkty krytyczne: x=-2\\;\\text{oraz}\\;x=4",
      "x<-2:\\quad -(x-4)-(x+2)=6\\Rightarrow -x+4-x-2=6\\Rightarrow -2x+2=6\\Rightarrow x=-2\\quad\\text{poza przedzialem}",
      "-2\\le x<4:\\quad -(x-4)+(x+2)=6\\Rightarrow -x+4+x+2=6\\Rightarrow 6=6",
      "x\\ge 4:\\quad (x-4)+(x+2)=6\\Rightarrow 2x-2=6\\Rightarrow x=4",
      "x\\in[-2,4]",
    ],
  },
  {
    id: "double-23",
    family: "double",
    equation: "\\left|2x-4\\right|+\\left|x+2\\right|=8",
    answerKind: "finite",
    solutions: [-2, 10 / 3],
    critical: [-2, 2],
    splitLatex: "2x-4,\\;x+2",
    intervals: ["x<-2", "-2\\le x<2", "x\\ge 2"],
    steps: [
      "Punkty krytyczne: x=-2\\;\\text{oraz}\\;x=2",
      "x<-2:\\quad -(2x-4)-(x+2)=8\\Rightarrow -2x+4-x-2=8\\Rightarrow -3x+2=8\\Rightarrow -3x=6\\Rightarrow x=-2\\quad\\text{poza przedzialem}",
      "-2\\le x<2:\\quad -(2x-4)+(x+2)=8\\Rightarrow -2x+4+x+2=8\\Rightarrow -x+6=8\\Rightarrow x=-2",
      "x\\ge 2:\\quad (2x-4)+(x+2)=8\\Rightarrow 3x-2=8\\Rightarrow 3x=10\\Rightarrow x=\\dfrac{10}{3}",
      "x\\in\\{-2,\\dfrac{10}{3}\\}",
    ],
  },
  {
    id: "double-24",
    family: "double",
    equation: "\\left|x+3\\right|+\\left|2-x\\right|=x+5",
    answerKind: "finite",
    solutions: [0, 4],
    critical: [-3, 2],
    splitLatex: "x+3,\\;2-x",
    intervals: ["x<-3", "-3\\le x<2", "x\\ge 2"],
    steps: [
      "Punkty krytyczne: x=-3\\;\\text{oraz}\\;x=2",
      "x<-3:\\quad -(x+3)+(2-x)=x+5\\Rightarrow -x-3+2-x=x+5\\Rightarrow -2x-1=x+5\\Rightarrow -3x=6\\Rightarrow x=-2\\quad\\text{poza przedzialem}",
      "-3\\le x<2:\\quad (x+3)+(2-x)=x+5\\Rightarrow 5=x+5\\Rightarrow x=0",
      "x\\ge 2:\\quad (x+3)-(2-x)=x+5\\Rightarrow x+3-2+x=x+5\\Rightarrow 2x+1=x+5\\Rightarrow x=4",
      "x\\in\\{0,4\\}",
    ],
  },
];

const NESTED_DETAILS = {
  "nested-1": [
    {
      label: "Pierwszy przypadek",
      start: "\\left|x\\right|-6=4",
      isolate: "\\left|x\\right|=10",
      branches: ["x=10", "x=-10"],
      solutions: [10, -10],
    },
    {
      label: "Drugi przypadek",
      start: "\\left|x\\right|-6=-4",
      isolate: "\\left|x\\right|=2",
      branches: ["x=2", "x=-2"],
      solutions: [2, -2],
    },
  ],
  "nested-2": [
    {
      label: "Pierwszy przypadek",
      start: "\\left|x\\right|-2=1",
      isolate: "\\left|x\\right|=3",
      branches: ["x=3", "x=-3"],
      solutions: [3, -3],
    },
    {
      label: "Drugi przypadek",
      start: "\\left|x\\right|-2=-1",
      isolate: "\\left|x\\right|=1",
      branches: ["x=1", "x=-1"],
      solutions: [1, -1],
    },
  ],
  "nested-3": [
    {
      label: "Pierwszy przypadek",
      start: "\\left|2x+5\\right|-4=3",
      isolate: "\\left|2x+5\\right|=7",
      branches: ["2x+5=7", "2x+5=-7"],
      solveLines: [["2x=2", "x=1"], ["2x=-12", "x=-6"]],
      solutions: [1, -6],
    },
    {
      label: "Drugi przypadek",
      start: "\\left|2x+5\\right|-4=-3",
      isolate: "\\left|2x+5\\right|=1",
      branches: ["2x+5=1", "2x+5=-1"],
      solveLines: [["2x=-4", "x=-2"], ["2x=-6", "x=-3"]],
      solutions: [-2, -3],
    },
  ],
  "nested-4": [
    {
      label: "Pierwszy przypadek",
      start: "7-\\left|2x-5\\right|=6",
      isolateSteps: ["-\\left|2x-5\\right|=-1", "\\left|2x-5\\right|=1"],
      branches: ["2x-5=1", "2x-5=-1"],
      solveLines: [["2x=6", "x=3"], ["2x=4", "x=2"]],
      solutions: [3, 2],
    },
    {
      label: "Drugi przypadek",
      start: "7-\\left|2x-5\\right|=-6",
      isolateSteps: ["-\\left|2x-5\\right|=-13", "\\left|2x-5\\right|=13"],
      branches: ["2x-5=13", "2x-5=-13"],
      solveLines: [["2x=18", "x=9"], ["2x=-8", "x=-4"]],
      solutions: [9, -4],
    },
  ],

  "nested-5": [
    {
      label: "Pierwszy przypadek",
      start: "\\left|2x+1\\right|-3=5",
      isolate: "\\left|2x+1\\right|=8",
      branches: ["2x+1=8", "2x+1=-8"],
      solveLines: [["2x=7", "x=\\dfrac{7}{2}"], ["2x=-9", "x=-\\dfrac{9}{2}"]],
      solutions: [3.5, -4.5],
    },
    {
      label: "Drugi przypadek",
      start: "\\left|2x+1\\right|-3=-5",
      isolate: "\\left|2x+1\\right|=-2",
      invalidReason: "Warto\u015b\u0107 bezwzgl\u0119dna nie mo\u017ce by\u0107 liczb\u0105 ujemn\u0105, wi\u0119c ten przypadek nie daje rozwi\u0105za\u0144.",
      solutions: [],
    },
  ],
  "nested-6": [
    {
      label: "Pierwszy przypadek",
      start: "5-\\left|3x-2\\right|=7",
      isolateSteps: ["-\\left|3x-2\\right|=2", "\\left|3x-2\\right|=-2"],
      invalidReason: "Warto\u015b\u0107 bezwzgl\u0119dna nie mo\u017ce by\u0107 liczb\u0105 ujemn\u0105, wi\u0119c ten przypadek nie daje rozwi\u0105za\u0144.",
      solutions: [],
    },
    {
      label: "Drugi przypadek",
      start: "5-\\left|3x-2\\right|=-7",
      isolateSteps: ["-\\left|3x-2\\right|=-12", "\\left|3x-2\\right|=12"],
      branches: ["3x-2=12", "3x-2=-12"],
      solveLines: [["3x=14", "x=\\dfrac{14}{3}"], ["3x=-10", "x=-\\dfrac{10}{3}"]],
      solutions: [14 / 3, -10 / 3],
    },
  ],
};

function pickVariant(family, slot, options = {}) {
  if (slot === "nested-simple") return makeNestedSimpleVariant();
  if (slot === "nested-linear") return makeNestedLinearVariant();
  if (family === "single" && !slot) {
    return makeControlledSingleVariant({ weighted: Boolean(options.weighted) });
  }
  if (family === "double" && !slot) return makeControlledDoubleVariant();
  if (family === "double-linear" && !slot) {
    return makeControlledDoubleLinearVariant({
      weighted: Boolean(options.weighted),
      constantRight: Boolean(options.constantRight),
    });
  }
  const pool = slot
    ? VARIANTS.filter((item) => item.slot === slot)
    : family
    ? VARIANTS.filter((item) => item.family === family)
    : VARIANTS;
  return pool[Math.floor(Math.random() * pool.length)];
}

function parseLinearExpression(latex) {
  const normalized = String(latex || "")
    .replace(/\\;/g, "")
    .replace(/\s+/g, "")
    .replace(/\\left\|/g, "")
    .replace(/\\right\|/g, "");
  const standard = normalized.match(/^([+-]?\d*)x([+-]\d+)?$/);
  if (standard) {
    const aRaw = standard[1];
    const a = aRaw === "" || aRaw === "+" ? 1 : aRaw === "-" ? -1 : Number(aRaw);
    const b = standard[2] ? Number(standard[2]) : 0;
    if (Number.isFinite(a) && a !== 0 && Number.isFinite(b)) return { a, b, root: -b / a };
  }
  const reversed = normalized.match(/^([+-]?\d+)([+-])(\d*)x$/);
  if (reversed) {
    const b = Number(reversed[1]);
    const coefficient = reversed[3] === "" ? 1 : Number(reversed[3]);
    const a = reversed[2] === "-" ? -coefficient : coefficient;
    if (Number.isFinite(a) && a !== 0) return { a, b, root: -b / a };
  }
  return null;
}

function plainNumberLabel(value) {
  return numberRaw(value);
}
function splitSignExpressions(data) {
  const expressions = String(data.splitLatex || "")
    .split(/,|\\;/)
    .map((item) => item.trim())
    .filter(Boolean);
  return expressions.map((expression, index) => {
    const parsed = parseLinearExpression(expression);
    const root = Number.isFinite(parsed?.root) ? parsed.root : data.critical?.[index];
    const a = Number.isFinite(parsed?.a) ? parsed.a : 1;
    return {
      expression,
      root,
      increasing: a > 0,
      leftSign: a > 0 ? "-" : "+",
      rightSign: a > 0 ? "+" : "-",
    };
  }).filter((item) => Number.isFinite(item.root));
}

function CombinedSignAxis({ items }) {
  const roots = uniqueSorted(items.map((item) => item.root));
  if (roots.length < 2) return null;
  const width = 680;
  const height = 218;
  const left = 68;
  const right = width - 42;
  const axisY = 108;
  const rootX = (root) => {
    const index = roots.findIndex((value) => Math.abs(value - root) < TOL);
    return left + ((index + 1) * (right - left)) / (roots.length + 1);
  };
  const segmentXs = [
    left + 54,
    ...roots.slice(0, -1).map((root, index) => (rootX(root) + rootX(roots[index + 1])) / 2),
    right - 64,
  ];
  const segmentProbe = (index) => (index === 0
    ? roots[0] - 1
    : index === roots.length
      ? roots[roots.length - 1] + 1
      : (roots[index - 1] + roots[index]) / 2);
  const signFor = (item, index) => {
    const probe = segmentProbe(index);
    return item.increasing ? (probe < item.root ? "-" : "+") : (probe < item.root ? "+" : "-");
  };
  const slopeMagnitude = (() => {
    const pattern = items.map((item) => (item.increasing ? "u" : "d")).join("");
    if (pattern === "ud" || pattern === "du") return 0.34;
    return 0.28;
  })();
  const lineY = (item, x) => {
    const slope = item.increasing ? -slopeMagnitude : slopeMagnitude;
    return axisY + slope * (x - rootX(item.root));
  };
  const badgeOffset = (item, row, index, sign) => {
    const sameSignRows = items
      .map((entry, entryRow) => ({ entry, entryRow, sign: signFor(entry, index) }))
      .filter((entry) => entry.sign === sign);
    if (sameSignRows.length < 2) return 0;
    const sameDirection = sameSignRows.every((entry) => entry.entry.increasing === item.increasing);
    if (!sameDirection) return item.increasing ? 34 : -34;
    const ordinal = Math.max(0, sameSignRows.findIndex((entry) => entry.entryRow === row));
    return ordinal === 0 ? -30 : 30;
  };
  const lineClearance = (x, y) => Math.min(...items.map((entry) => Math.abs(y - lineY(entry, x))));
  const pickBadgeY = (sign, x, item) => {
    const lineAtBadge = lineY(item, x);
    const pocketY = axisY + (lineAtBadge - axisY) * 0.5;
    const upper = [axisY - 46, axisY - 66, axisY - 84];
    const lower = [axisY + 46, axisY + 66, axisY + 84];
    const pocketIsOnSignSide = sign === "+" ? pocketY < axisY : pocketY > axisY;
    const pocketFirst = Math.abs(lineAtBadge - axisY) >= 52 && pocketIsOnSignSide ? [pocketY] : [];
    const fallback = sign === "+" ? lower : upper;
    const candidates = sign === "+"
      ? [...pocketFirst, ...upper, ...fallback]
      : [...pocketFirst, ...lower, ...fallback];
    let best = candidates[0];
    let bestScore = Infinity;
    candidates.forEach((candidateY, index) => {
      const y = Math.max(30, Math.min(height - 30, candidateY));
      const clearance = lineClearance(x, y);
      const axisClearance = Math.abs(y - axisY);
      const collisionPenalty = clearance < 24 ? (24 - clearance) * 120 : 0;
      const axisPenalty = axisClearance < 26 ? (26 - axisClearance) * 40 : 0;
      const preferencePenalty = index * 6;
      const score = collisionPenalty + axisPenalty + preferencePenalty;
      if (score < bestScore) {
        bestScore = score;
        best = y;
      }
    });
    return best;
  };
  const badgePosition = (item, row, index, x) => {
    const sign = signFor(item, index);
    const xShift = badgeOffset(item, row, index, sign);
    const badgeX = x + xShift;
    return {
      x: badgeX,
      y: pickBadgeY(sign, badgeX, item),
      sign,
    };
  };
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", justifyContent: "center", marginBottom: 8 }}>
        {items.map((item) => (
          <InlineMath key={item.expression} math={`${item.expression}=0\\iff x=${numberLatex(item.root, { mixed: true })}`} />
        ))}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="Wspolny szkic znakow wyrazen liniowych">
        <line x1={left} y1={axisY} x2={right} y2={axisY} stroke="currentColor" strokeWidth="2" />
        <polygon points={`${right},${axisY} ${right - 12},${axisY - 7} ${right - 12},${axisY + 7}`} fill="currentColor" />
        {roots.map((root) => {
          const x = rootX(root);
          return (
            <g key={`root-${root}`}>
              <line x1={x} y1={axisY - 7} x2={x} y2={axisY + 7} stroke="currentColor" strokeWidth="2" />
              <text x={x} y={axisY + 30} textAnchor="middle" fontSize="19" fontFamily="KaTeX_Main, STIXGeneral, Times New Roman, serif" fill="currentColor">{plainNumberLabel(root)}</text>
            </g>
          );
        })}
        {items.map((item, row) => {
          const x0 = rootX(item.root);
          const lineLeft = left + 30;
          const lineRight = right - 30;
          const color = row === 0 ? "var(--color-accent, #f59e0b)" : "#8b5cf6";
          return (
            <g key={item.expression}>
              <line
                x1={lineLeft}
                y1={lineY(item, lineLeft)}
                x2={lineRight}
                y2={lineY(item, lineRight)}
                stroke={color}
                strokeWidth="4"
                strokeLinecap="round"
              />
              <circle cx={x0} cy={axisY} r="4" fill={color} />
              {segmentXs.map((x, index) => {
                const badge = badgePosition(item, row, index, x);
                return (
                  <g key={`${item.expression}-${index}`}>
                    <circle cx={badge.x} cy={badge.y} r="13" fill="rgba(0,0,0,.18)" stroke={color} strokeWidth="2" />
                    <text x={badge.x} y={badge.y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="20" fontWeight="700" fontFamily="KaTeX_Main, STIXGeneral, Times New Roman, serif" fill={color}>{badge.sign}</text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <p style={{ margin: "0 0 2px", fontSize: "0.95rem", textAlign: "center", opacity: 0.9 }}>
        {"Miejsca zerowe wyra\u017ce\u0144 pod modu\u0142ami dziel\u0105 o\u015b na przedzia\u0142y. Plus czytamy nad osi\u0105, minus pod osi\u0105."}
      </p>
    </div>
  );
}

function SignSplit({ data }) {
  if (!data.critical?.length) return null;
  const items = splitSignExpressions(data);
  const points = data.critical.slice(0, 2).sort((a, b) => a - b);
  return (
    <div style={{ margin: "12px 0", padding: "12px 14px", border: "1px solid rgba(255,255,255,.24)", borderRadius: 8, maxWidth: 760 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{"Znaki wyra\u017ce\u0144 pod warto\u015bci\u0105 bezwzgl\u0119dn\u0105"}</div>
      {items.length > 1 ? (
        <CombinedSignAxis items={items} />
      ) : items.length === 1 ? (
        <div style={{ maxWidth: 420, margin: "0 auto", textAlign: "center" }}>
          {items.map((item) => (
            <div key={item.expression}>
              <p style={{ margin: "2px 0 6px" }}>
                <InlineMath math={`${item.expression}=0\\iff x=${numberLatex(item.root, { mixed: true })}`} />
              </p>
              <LineSignShape
                increasing={item.increasing}
                zeroLabel={plainNumberLabel(item.root)}
                leftSign={item.leftSign}
                rightSign={item.rightSign}
                width={320}
                height={150}
              />
              <p style={{ margin: "4px 0 0", fontSize: "0.95rem" }}>
                <InlineMath math={`x<${numberLatex(item.root, { mixed: true })}`} />{" : znak "}<strong>{item.leftSign}</strong>{", "}
                <InlineMath math={`x>${numberLatex(item.root, { mixed: true })}`} />{" : znak "}<strong>{item.rightSign}</strong>{"."}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <svg viewBox="0 0 620 76" width="100%" height="76" role="img" aria-label="Podzia\u0142 osi">
          <line x1="42" y1="34" x2="578" y2="34" stroke="currentColor" strokeWidth="2" />
          <polygon points="578,34 566,27 566,41" fill="currentColor" />
          {points.map((point, index) => {
            const x = points.length === 1 ? 310 : 220 + index * 180;
            return (
              <g key={`${point}-${index}`}>
                <line x1={x} y1="18" x2={x} y2="50" stroke="var(--color-accent, #ff9900)" strokeWidth="3" />
                <text x={x} y="70" textAnchor="middle" fill="currentColor" fontSize="18">{numberRaw(point)}</text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function IntervalsList({ intervals = [] }) {
  if (!intervals.length) return null;
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
      <span>{"Przedzia\u0142y:"}</span>
      {intervals.map((interval) => (
        <span key={interval} style={{ padding: "3px 8px", border: "1px solid rgba(255,255,255,.22)", borderRadius: 999 }}>
          <InlineMath math={interval} />
        </span>
      ))}
    </div>
  );
}

function splitCaseStep(step) {
  const match = String(step).match(/^(.+?):\\quad\s*(.+)$/);
  if (!match) return null;
  const interval = match[1];
  const body = match[2];
  const parts = body.split(/\\Rightarrow/).map((part) => part.trim()).filter(Boolean);
  return { interval, equation: parts[0] || body, solution: parts.slice(1).join("\\Rightarrow ") };
}

function solutionStatus(solution) {
  if (/poza przedzialem/.test(solution)) {
    return {
      kind: "outside",
      text: "Otrzymany wynik nie nale\u017cy do rozwa\u017canego przedzia\u0142u, wi\u0119c odrzucamy go.",
    };
  }
  if (/sprzecznosc/.test(solution)) {
    return {
      kind: "contradiction",
      text: "Otrzymujemy sprzeczno\u015b\u0107, wi\u0119c ten przedzia\u0142 nie daje rozwi\u0105za\u0144.",
    };
  }
  if (/\b(-?\d+)\s*=\s*\1\b/.test(solution)) {
    return {
      kind: "identity",
      text: "Dostajemy r\u00f3wno\u015b\u0107 prawdziw\u0105, wi\u0119c ca\u0142y rozwa\u017cany przedzia\u0142 spe\u0142nia r\u00f3wnanie.",
    };
  }
  return null;
}

function cleanSolutionLine(line) {
  return String(line)
    .replace(/\\quad\\text\{poza przedzialem\}/g, "")
    .replace(/\\text\{sprzecznosc\}/g, "\\text{sprzeczno\u015b\u0107}")
    .trim();
}

function CaseSolution({ step, index }) {
  const parsed = splitCaseStep(step);
  if (!parsed) return <BlockMath math={step} />;
  const status = solutionStatus(parsed.solution);
  const solutionLines = parsed.solution
    ? parsed.solution.split(/\\Rightarrow/).map(cleanSolutionLine).filter(Boolean)
    : [];
  return (
    <div style={{ margin: "16px 0", paddingLeft: 12, borderLeft: "3px solid var(--color-accent, #f59e0b)" }}>
      <p style={{ marginBottom: 6 }}>
        <strong>{"Przedzia\u0142 "}{index + 1}{": "}</strong>
        <InlineMath math={parsed.interval} />
      </p>
      <p style={{ margin: "4px 0" }}>{"W tym przedziale r\u00f3wnanie ma posta\u0107:"}</p>
      <div style={{ textAlign: "center" }}><BlockMath math={parsed.equation} /></div>
      {solutionLines.length ? (
        <>
          <p style={{ margin: "4px 0" }}>{"Rozwi\u0105zujemy r\u00f3wnanie krok po kroku:"}</p>
          <div style={{ textAlign: "center" }}>
            {solutionLines.map((line, lineIndex) => (
              <BlockMath key={`${parsed.interval}-solution-${lineIndex}`} math={line} />
            ))}
          </div>
          <p style={{ margin: "6px 0 0" }}>
            {status ? status.text : "Otrzymany wynik nale\u017cy do rozwa\u017canego przedzia\u0142u, wi\u0119c zostaje w zbiorze rozwi\u0105za\u0144."}
          </p>
        </>
      ) : null}
    </div>
  );
}

export function generateTask(options = {}) {
  const family = typeof options === "string" ? options : options.family;
  const slot = typeof options === "object" ? options.slot : null;
  const variant = pickVariant(family, slot, options);
  return {
    type: "AdvancedAbsoluteEquationTask",
    data: variant,
    answer: variant.answerKind === "finite" ? variant.solutions : variant.interval,
    maxPoints: 1,
  };
}

export function renderTask({ data }) {
  return (
    <div>
      <p>{"Rozwi\u0105\u017c algebraicznie r\u00f3wnanie:"}</p>
      <div style={{ textAlign: "center", margin: "10px 0 14px" }}>
        <BlockMath math={data.equation} />
      </div>
    </div>
  );
}

export function renderInput(task, userInput, onChange, disabled) {
  const value = typeof userInput === "string" ? userInput : userInput?.raw || "";
  return (
    <div>
      <IntervalInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        asString
        placeholder="np. {-2; 1/3; 4} albo [-3; 1]"
        label="zbior rozwiazan"
        prefixAbove
      />
    </div>
  );
}

export function checkAnswer({ data }, userInput) {
  const raw = typeof userInput === "string" ? userInput : userInput?.raw || "";
  if (data.answerKind === "interval") {
    const parsed = parseIntervalInput(raw);
    return {
      correct: sameInterval(parsed, data.interval),
      expected: data.interval,
      maxScore: 1,
      score: sameInterval(parsed, data.interval) ? 1 : 0,
    };
  }
  const parsed = parseFiniteSet(raw);
  const correct = sameFiniteSet(parsed, data.solutions);
  return { correct, expected: data.solutions, maxScore: 1, score: correct ? 1 : 0 };
}

export function renderAnswer({ data }) {
  if (data.answerKind === "interval") {
    return <InlineMath math={`x\\in ${intervalLatex(data.interval)}`} />;
  }
  return <InlineMath math={`x\\in ${setLatex(data.solutions, { mixed: true, sort: true })}`} />;
}

export function renderUserAnswer(userInput, task) {
  const raw = typeof userInput === "string" ? userInput : userInput?.raw || "";
  if (!raw) return <i>{"(brak odpowiedzi)"}</i>;
  if (task?.data?.answerKind === "interval") {
    return <InlineMath math={intervalToLatex(raw, { withX: true }) || String(raw)} />;
  }
  const parsed = parseFiniteSet(raw);
  if (!parsed) return <span>{raw}</span>;
  return <InlineMath math={`x\\in ${setLatex(parsed, { mixed: true, sort: true })}`} />;
}

function RenderNestedCase({ item }) {
  const isolateSteps = item.isolateSteps || [item.isolate];
  const branches = item.branches || [];
  const singleBranch = branches.length === 1;
  return (
    <div style={{ margin: "16px 0", paddingLeft: 12, borderLeft: "3px solid var(--color-accent, #ff9900)" }}>
      <p style={{ marginBottom: 6 }}><strong>{item.label}</strong></p>
      <p style={{ margin: "4px 0" }}>{"Po zdj\u0119ciu zewn\u0119trznej warto\u015bci bezwzgl\u0119dnej otrzymujemy:"}</p>
      <div style={{ textAlign: "center" }}><BlockMath math={item.start} /></div>
      <p style={{ margin: "4px 0" }}>{"Izolujemy wewn\u0119trzn\u0105 warto\u015b\u0107 bezwzgl\u0119dn\u0105:"}</p>
      <div style={{ textAlign: "center" }}>
        {isolateSteps.map((line, index) => <BlockMath key={`${item.label}-iso-${index}`} math={line} />)}
      </div>
      {item.invalidReason ? (
        <p style={{ marginTop: 8, fontWeight: 700 }}>{item.invalidReason}</p>
      ) : (
        <>
          <p style={{ margin: "4px 0" }}>{singleBranch ? "Rozpisujemy modu\u0142 na jedno r\u00f3wnanie liniowe:" : "Rozpisujemy modu\u0142 na dwa r\u00f3wnania liniowe:"}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            {branches.map((branch, index) => (
              <div key={`${item.label}-branch-${index}`} style={{ textAlign: "center" }}>
                <BlockMath math={branch} />
                {(item.solveLines?.[index] || []).map((line, lineIndex) => (
                  <BlockMath key={`${item.label}-line-${index}-${lineIndex}`} math={line} />
                ))}
              </div>
            ))}
          </div>
          <p style={{ marginTop: 8 }}>{"Z tego przypadku dostajemy:"}</p>
          <div style={{ textAlign: "center" }}>
            <BlockMath math={`x\\in ${setLatex(item.solutions, { mixed: true, sort: true })}`} />
          </div>
        </>
      )}
    </div>
  );
}

function renderNestedExplanation(data) {
  const details = data.nestedDetails || NESTED_DETAILS[data.id] || [];
  return (
    <div className="space-y-4">
      <p><strong>{"Krok 1:"}</strong>{" Zdejmujemy zewn\u0119trzn\u0105 warto\u015b\u0107 bezwzgl\u0119dn\u0105, wi\u0119c dostajemy dwa przypadki."}</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={data.steps?.[0] || data.equation} />
      </div>
      <p><strong>{"Krok 2:"}</strong>{" Ka\u017cdy z otrzymanych przypadk\u00f3w rozwi\u0105zujemy jak osobne proste r\u00f3wnanie z warto\u015bci\u0105 bezwzgl\u0119dn\u0105."}</p>
      {details.map((item) => <RenderNestedCase key={item.label} item={item} />)}
      <p><strong>{"Krok 3:"}</strong>{" \u0141\u0105czymy rozwi\u0105zania z obu przypadk\u00f3w."}</p>
      <div style={{ textAlign: "center" }}>{renderAnswer({ data })}</div>
    </div>
  );
}

export function renderExplanation({ data }) {
  if (data.family === "nested") {
    return renderNestedExplanation(data);
  }

  const pointStep = (data.steps || []).find((step) => step.startsWith("Punkty krytyczne"));
  const signStep = !pointStep ? data.steps?.[0] : null;
  const caseSteps = (data.steps || []).filter((step) => splitCaseStep(step));
  const finalSteps = (data.steps || []).filter((step) => !splitCaseStep(step) && step !== pointStep && step !== signStep);

  return (
    <div className="space-y-4">
      <p><strong>{"Krok 1:"}</strong>{" Wyznaczamy punkty krytyczne i rozbijamy o\u015b na przedzia\u0142y."}</p>
      <SignSplit data={data} />
      <IntervalsList intervals={data.intervals} />
      {data.splitLatex && (
        <p>{"O znaku decyduj\u0105 wyra\u017cenia: "}<InlineMath math={data.splitLatex} />{"."}</p>
      )}
      {pointStep && <div style={{ textAlign: "center" }}><BlockMath math={pointStep.replace(/^Punkty krytyczne:\s*/, "")} /></div>}
      {signStep && <div style={{ textAlign: "center" }}><BlockMath math={signStep} /></div>}

      <p><strong>{"Krok 2:"}</strong>{" Rozwi\u0105zujemy r\u00f3wnanie osobno w ka\u017cdym przedziale."}</p>
      {caseSteps.map((step, index) => <CaseSolution key={`${data.id}-case-${index}`} step={step} index={index} />)}

      {finalSteps.length ? (
        <>
          <p><strong>{"Krok 3:"}</strong>{" Zbieramy wyniki, kt\u00f3re spe\u0142niaj\u0105 warunki przedzia\u0142\u00f3w."}</p>
          <div style={{ textAlign: "center" }}>
            {finalSteps.map((step, index) => <BlockMath key={`${data.id}-final-${index}`} math={step} />)}
          </div>
        </>
      ) : null}

      <p><strong>{"Odpowied\u017a ko\u0144cowa:"}</strong></p>
      <div style={{ textAlign: "center" }}>{renderAnswer({ data })}</div>
    </div>
  );
}




